import { type SeriesSummary } from "@crowdcoursing/api/openapi";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
} from "@mui/material";
import { useEffect, useState } from "react";

import { CharacterLimitedTextField as TextField } from "~ui/atoms/CharacterLimitedTextField/CharacterLimitedTextField";

// User-facing strings
const STRINGS = {
  ADD_SERIES: "Add Series",
  CANCEL: "Cancel",
  DELETE_SERIES: "Delete series",
  DESCRIPTION_LABEL: "Description",
  MANAGE_SERIES: "Manage Event Series",

  NAME_LABEL: "Name",

  SAVE: "Save",
} as const;

import { type SeriesChange } from "~hooks/useEventSeries";

export interface EventSeriesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (changes: SeriesChange) => void | Promise<void>;
  series: SeriesSummary[];
}

export const EventSeriesDialog = ({ open, onClose, onSave, series: initialSeries }: EventSeriesDialogProps) => {
  const [series, setSeries] = useState<SeriesSummary[]>(initialSeries);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update series when initialSeries changes or modal opens/closes
  useEffect(() => {
    setSeries(initialSeries);
    setHasChanges(false);
    setIsSaving(false);
  }, [initialSeries, open]);
  const [newSeries, setNewSeries] = useState<{ name: string; description: string }>({
    description: "",
    name: "",
  });

  const handleAddSeries = () => {
    if (newSeries.name && newSeries.description) {
      setSeries([
        ...series,
        {
          description: newSeries.description,
          id: `new-${Date.now()}`,
          name: newSeries.name,
        },
      ]);
      setNewSeries({ description: "", name: "" });
      setHasChanges(true);
    }
  };

  const handleDeleteSeries = (seriesId: string) => {
    setSeries(series.filter((s) => s.id !== seriesId));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Find series that were added (have temporary IDs)
    const added = series
      .filter((s) => s.id?.startsWith("new-"))
      .map((s) => ({ description: s.description || null, name: s.name || null }));

    // Find series that were removed (in initialSeries but not in current series)
    const removed = initialSeries
      .filter((s) => !series.some((cs) => cs.id === s.id))
      .map((s) => ({ id: s.id || null }));

    setIsSaving(true);
    try {
      await onSave({ added, removed });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle data-testid="series-dialog-title">{STRINGS.MANAGE_SERIES}</DialogTitle>
      <DialogContent>
        <List data-testid="series-list">
          {[...series]
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
            .map((s, index) => (
              <ListItem
                key={s.id || index}
                data-testid="series-item"
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  py: 2,
                }}
              >
                <ListItemText primary={s.name} secondary={s.description} />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={STRINGS.DELETE_SERIES}
                    onClick={() => handleDeleteSeries(s.id || "")}
                    disabled={isSaving}
                    data-testid="delete-series-button"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>

        <Box sx={{ mt: 3 }} data-testid="new-series-form">
          <TextField
            fullWidth
            inputProps={{ maxLength: 50 }}
            label={STRINGS.NAME_LABEL}
            value={newSeries.name}
            onChange={(e) => setNewSeries({ ...newSeries, name: e.target.value })}
            sx={{ mb: 2 }}
            data-testid="series-name-input"
          />
          <TextField
            fullWidth
            inputProps={{ maxLength: 250 }}
            label={STRINGS.DESCRIPTION_LABEL}
            value={newSeries.description}
            onChange={(e) => setNewSeries({ ...newSeries, description: e.target.value })}
            multiline
            rows={2}
            data-testid="series-description-input"
          />
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddSeries}
            disabled={!newSeries.name || !newSeries.description || isSaving}
            sx={{ mt: 1 }}
            data-testid="add-series-button"
          >
            {STRINGS.ADD_SERIES}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="cancel-button" disabled={isSaving}>
          {STRINGS.CANCEL}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasChanges || isSaving} data-testid="save-button">
          {STRINGS.SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
